// Tool-service descriptors (arc 101 slice 07) — the connector family whose
// whole delivery is DERIVED, not hand-written.
//
// A "tool service" is an external service the coding agent reaches AS AN MCP
// SERVER (ui-debugger today; other debugging/observability services next). The
// human connects it once in the dashboard; the box gets it as an `.mcp.json`
// entry with a working `env` block whose values are `${VAR}` references into the
// box's own process env — so the credential reaches the agent without ever
// touching a prompt, a log, or a committed file.
//
// Everything a tool service needs is ONE entry in {@link TOOL_SERVICE_DESCRIPTORS}:
//
//   slug     the connector handle — becomes the `tool_service:<slug>` integration
//            kind AND the `integration:<slug>` vault name (`integration.ts`).
//   fields   the connect form (`CONNECTOR_FIELDS` derives from these) and the
//            connect payload's Zod shape (`ConnectIntegrationSchema` derives too).
//   env      box process-env var name → the field whose value it carries. The
//            assigner seals these; the box unseals them to process env ONLY
//            (`sealed` exposure, never a file — slice 06's wire).
//   mcp      the `.mcp.json` server: its key, argv, and server-env → BOX-env-var
//            NAME map. Values name a var, they are never a value: a literal
//            credential is not expressible here, which is the point.
//   requires the runner capabilities the box must advertise before the server is
//            written at all (`ui-debugger` ⇒ `browsers`, the gate it has today).
//
// Adding a service is therefore one descriptor + one locale key — no schema
// edit, no union member, no form change. Pure data: no I/O, no rendering. The
// `.mcp.json` render lives in `@developerz/mcp` (`buildManagedMcpServers`), the
// seal in `apps/worker`, the unseal in `apps/runner` — each reads this registry.

import { z } from 'zod';
import { RunnerCapability } from './runner';

/**
 * A field's declared VALUE shape, enforced by the derived `ConnectIntegrationSchema`
 * (`integration.ts`). Absent ⇒ any non-empty string.
 *
 * `hostname` — a bare host, optionally `:port`. No scheme, no path, no query, no
 * userinfo. It exists because a hostname var is pasted as a URL by reflex, and the
 * value lands in the box env a server reads verbatim: `SENTRY_HOST=https://x/api`
 * builds `https://https://x/api/api/0/…`, which fails as an opaque network error at
 * agent runtime rather than at the connect form where the human can fix it.
 */
export type ToolServiceFieldFormat = 'hostname';

/** One field of a tool service's connect form. Snake_case — it is the JSON key. */
export type ToolServiceField = {
  /** JSON key in the connect payload's `config` (non-secret) or `secret` group. */
  readonly name: string;
  /** Secret ⇒ password input, sealed into the vault, never echoed. */
  readonly secret: boolean;
  readonly required: boolean;
  /**
   * Applied when the human leaves the field blank. NON-SECRET fields only — a
   * defaulted credential is a credential nobody chose (invariant, tested). A
   * default must itself satisfy the field's {@link format} (invariant, tested).
   */
  readonly default?: string;
  /** Narrows the accepted value beyond "non-empty string". */
  readonly format?: ToolServiceFieldFormat;
};

/** Longest legal hostname, plus `:65535`. */
export const TOOL_SERVICE_HOSTNAME_MAX = 253 + 6;

const HOSTNAME_LABEL = '[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?';

/**
 * Bare host, optionally `:port`. Rejects the scheme (`https://…`), the path
 * (`sentry.io/api/0`), the query, and userinfo (`user@host`) — each of which a
 * bare `z.string()` would carry straight through to the box env.
 *
 * A port IS allowed: a self-hosted instance on a non-443 port is a real
 * deployment, and it is not one of the mistakes the field name guards against.
 */
/**
 * 1..65535, no leading zeros — a RANGE, not `\d{1,5}`, which is what this used
 * to be. `:0`, `:65536`, `:99999` and `:00443` are ports no socket can carry, and
 * the value reaches the vendor server verbatim through the box env, so each one
 * became the opaque agent-runtime failure this whole format exists to move back
 * to the connect form. `:00443` earns its place separately: it and `:443` reach
 * the server as different strings and only one is what the human meant.
 */
const PORT_1_TO_65535 =
  '(?:[1-9]\\d{0,3}|[1-5]\\d{4}|6[0-4]\\d{3}|65[0-4]\\d{2}|655[0-2]\\d|6553[0-5])';

export const TOOL_SERVICE_HOSTNAME_RE = new RegExp(
  `^${HOSTNAME_LABEL}(?:\\.${HOSTNAME_LABEL})*(?::${PORT_1_TO_65535})?$`,
);

/**
 * True when `value` is a bare hostname (see {@link TOOL_SERVICE_HOSTNAME_RE}).
 *
 * The whole rule lives in that ONE regex, deliberately: `integration.ts`'s
 * `FORMAT_SCHEMA.hostname` — the gate the connect route actually runs — applies
 * the same constant, so a bound added here cannot reach only the descriptor
 * invariant test and miss the seam a human types into.
 */
export function isToolServiceHostname(value: string): boolean {
  if (value.length === 0 || value.length > TOOL_SERVICE_HOSTNAME_MAX) return false;
  return TOOL_SERVICE_HOSTNAME_RE.test(value);
}

/**
 * The `.mcp.json` server one tool service contributes. `env` maps the server
 * process's OWN env var name → the BOX env var NAME holding the value (a key of
 * the descriptor's `env`). Never a value: the renderer emits `${BOX_VAR}`, so a
 * cloned repo carries an inert reference — the same posture as
 * `${DEVELOPERZ_API_KEY}` on the platform server.
 */
export type ToolServiceMcp = {
  /** Key under `mcpServers` — collides with nothing managed; merge never clobbers. */
  readonly key: string;
  readonly type: 'stdio';
  readonly command: string;
  readonly args?: readonly string[];
  /** server-env var name → box-env var name (NOT a value). */
  readonly env: Readonly<Record<string, string>>;
};

/**
 * A tool service's per-repo CONFIG file — workspace dirs, named browser targets and
 * their URLs for `ui-debugger`. Config, NOT secret: it is committed, it is written
 * by setup mode establish-or-verify (never clobbered), and the values come from the
 * repo's own `.maintainer.yml` `tool_services:` block. The credential half never
 * touches this file — it stays in the process env the `.mcp.json` `${VAR}`
 * references resolve against.
 */
export type ToolServiceRepoConfig = {
  /**
   * Repo-root-relative path of the rendered file
   * (`.dz/ui-debugger/ui-debugger-mcp.json`). Homed under `.dz/`, never the repo
   * root — the customer's root is theirs (#2964).
   */
  readonly path: string;
  /** Repo-root-relative path of the committed teaching companion (`…​.example`). */
  readonly examplePath: string;
  /**
   * The `.example` document, verbatim. A rendered STRING rather than an object
   * because it is a teaching artifact: it may carry the ordering and the comment-ish
   * shape a maintainer copies from, and nothing on the box re-serializes it.
   */
  readonly example: string;
};

/** One connectable tool service. See the file header for the contract. */
export type ToolServiceDescriptor = {
  /** Lowercase handle: `[a-z0-9][a-z0-9_-]*`. Unique, and never a base connector kind. */
  readonly slug: string;
  readonly fields: readonly ToolServiceField[];
  /** box env var name → field name. */
  readonly env: Readonly<Record<string, string>>;
  readonly mcp: ToolServiceMcp;
  /** Box capabilities required before the server is provisioned at all. */
  readonly requires: readonly string[];
  /**
   * The per-repo config file setup mode establishes beside `.mcp.json`, when the
   * service needs one. Absent ⇒ the service is configured entirely by its env block
   * and setup writes no second file.
   */
  readonly repoConfig?: ToolServiceRepoConfig;
};

/**
 * Descriptor #1 — the autonomous UI-testing MCP server (`@developerz.ai/ui-debugger-mcp`,
 * the on-box binary). It speaks the OpenAI wire, so its server env is
 * `OPENAI_*` while the BOX vars stay namespaced per service — one connected
 * service can never shadow another's key. Gated on `browsers`: the same
 * capability that gates it today (`buildManagedMcpServers`).
 */
const UI_DEBUGGER = {
  slug: 'ui-debugger',
  fields: [
    { name: 'api_key', secret: true, required: true },
    // OpenAI-compatible gateway; this repo's own committed `.mcp.json` points at
    // OpenRouter (#1405). Non-secret, so it rides `config` on the row.
    { name: 'base_url', secret: false, required: false, default: 'https://openrouter.ai/api/v1' },
  ],
  env: { UI_DEBUGGER_API_KEY: 'api_key', UI_DEBUGGER_BASE_URL: 'base_url' },
  mcp: {
    key: 'ui-debugger',
    type: 'stdio',
    command: 'ui-debugger-mcp',
    env: { OPENAI_API_KEY: 'UI_DEBUGGER_API_KEY', OPENAI_BASE_URL: 'UI_DEBUGGER_BASE_URL' },
  },
  requires: [RunnerCapability.Browsers],
  repoConfig: {
    // Homed under `.dz/`, never the repo root (#2964, parent #2958): a customer's
    // root is theirs, and every dotfile we add there is one they did not ask for.
    // The tool reads this location AHEAD of its old root file
    // (developerz-ai/ui-debugger-mcp#64), so the platform writes exactly one path
    // and a maintainer's pre-move root copy is left alone rather than kept in step.
    path: '.dz/ui-debugger/ui-debugger-mcp.json',
    examplePath: '.dz/ui-debugger/ui-debugger-mcp.json.example',
    // The shape of this repo's own operator-local file, with ONE target: a
    // maintainer copies it, renames the target and points the url at their dev
    // server. Deliberately not a guess at the repo's real ports — a target whose
    // url 404s teaches the agent that the app is broken.
    example: `${JSON.stringify(
      {
        workspace: './tmp/ui-debugger-mcp',
        targets: { app: { adapter: 'browser', headless: true, url: 'http://localhost:3000' } },
      },
      null,
      2,
    )}\n`,
  },
} as const satisfies ToolServiceDescriptor;

/**
 * Descriptor #2 — Sentry's OWN MCP server (`@sentry/mcp-server`), so the agent fixing a
 * crash can read the issue, its events and its stack traces itself instead of working
 * from whatever the task brief quoted. The service the platform already models
 * end-to-end (a `sentry` connection on `POST /hooks/:source_id` → a `raw_tasks` row
 * → triage → the bespoke `sentry` connector) is the honest second one to hand the
 * BOX.
 *
 * Registered as `sentry-mcp`, never `sentry`, for two independent reasons: the vault
 * keys on the slug, so `sentry` would seal over the bespoke connector's own credential
 * (a registry test refuses it); and `sentry` is the key the REMOTE half is reserved
 * under (`ManagedMcpOptions.integrationServers`, #311 — Sentry's hosted
 * `mcp.sentry.dev`). The two are the same vendor reached two ways, and this is the way
 * that needs no transport: run the vendor's own server locally against a sealed token.
 *
 * Deliberately DIFFERENT from descriptor #1 on every optional axis, because that is
 * what makes it a proof rather than a copy: it carries `args` (the first descriptor to
 * exercise the renderer's argv branch), declares NO `repoConfig` (it is configured
 * entirely by its env block, so setup mode writes no second file), and `requires`
 * NOTHING — an API client needs no browsers, no display, no docker. The ungated path
 * was anticipated (`boxHostsToolService`'s `unGated` case) but had no live instance
 * until now; the cost it implies — a server present in every managed `.mcp.json`
 * whether or not the account connected it — is exactly what the per-role eager tool cap
 * (`@developerz/agent` `mcp/mount.ts`) was built to absorb, and the entry stays inert
 * until the sealed `${SENTRY_ACCESS_TOKEN}` is actually set.
 *
 * `bunx`, not the banned `npx`: our boxes are Bun-only (bun is installed at enroll), and
 * this server is vendor-distributed rather than an on-box binary we provision. Left
 * untagged so a box reuses its cached resolve instead of re-resolving `@latest` on every
 * agent boot.
 */
const SENTRY_MCP = {
  slug: 'sentry-mcp',
  fields: [
    // Sentry's own name for it: a User Auth Token. Shares the bespoke connector's field
    // name (and so its dashboard label) — the same credential shape, a different holder.
    { name: 'auth_token', secret: true, required: true },
    // A HOST, not a url (`SENTRY_HOST` takes `sentry.io` / `us.sentry.io` / a self-hosted
    // hostname) — named `host` rather than reusing `base_url` precisely because passing a
    // `https://…/v1` here is the mistake the field name should prevent. The name is the
    // hint; `format: 'hostname'` is the enforcement, since a name cannot refuse a paste.
    { name: 'host', secret: false, required: false, default: 'sentry.io', format: 'hostname' },
  ],
  env: { SENTRY_ACCESS_TOKEN: 'auth_token', SENTRY_HOST: 'host' },
  mcp: {
    key: 'sentry-mcp',
    type: 'stdio',
    command: 'bunx',
    args: ['@sentry/mcp-server'],
    // The server reads the same var names the box holds them under — a service whose
    // own namespace is already the vendor's needs no second one, unlike `ui-debugger`
    // above, whose OpenAI-wire vars would otherwise collide with the box's inference env.
    env: { SENTRY_ACCESS_TOKEN: 'SENTRY_ACCESS_TOKEN', SENTRY_HOST: 'SENTRY_HOST' },
  },
  requires: [],
} as const satisfies ToolServiceDescriptor;

/**
 * Every connectable tool service, in catalog order. ONE entry per service.
 *
 * `as const satisfies` rather than an annotation on purpose: the annotation
 * would widen every `slug` to `string` and collapse {@link ToolServiceSlug} —
 * and with it `IntegrationKind` — into bare `string`, so a typo'd kind would
 * typecheck everywhere.
 */
export const TOOL_SERVICE_DESCRIPTORS = [UI_DEBUGGER, SENTRY_MCP] as const satisfies readonly [
  ToolServiceDescriptor,
  ...ToolServiceDescriptor[],
];

/**
 * The registry at its DECLARED type — iterate THIS whenever you read an OPTIONAL
 * field (`repoConfig`, `mcp.args`).
 *
 * The literal export above is `as const satisfies` on purpose, and the cost of that
 * is real: an entry declaring no `repoConfig` has no such PROPERTY on its literal
 * type, so `descriptor.repoConfig` is a type ERROR rather than `undefined` — which
 * only shows up once a second descriptor disagrees with the first about what it
 * declares. Same array, widened once, stated here instead of re-aliased at every
 * consumer.
 */
export const TOOL_SERVICES: readonly ToolServiceDescriptor[] = TOOL_SERVICE_DESCRIPTORS;

/** The registered service handles — the literal union, derived from the registry. */
export type ToolServiceSlug = (typeof TOOL_SERVICE_DESCRIPTORS)[number]['slug'];

/** The slugs, in catalog order. */
export const TOOL_SERVICE_SLUGS: readonly ToolServiceSlug[] = TOOL_SERVICE_DESCRIPTORS.map(
  (d) => d.slug,
);

/**
 * A registered slug, as a parse gate — for surfaces that take one from OUTSIDE the
 * process (a repo's `.maintainer.yml` `tool_services:` key, a REST param). Built
 * from the catalog, so an unregistered service is refused rather than silently
 * carried as free text.
 */
export const ToolServiceSlugSchema = z.enum(
  TOOL_SERVICE_SLUGS as [ToolServiceSlug, ...ToolServiceSlug[]],
);

const BY_SLUG: ReadonlyMap<string, ToolServiceDescriptor> = new Map(
  TOOL_SERVICE_DESCRIPTORS.map((d) => [d.slug, d] as const),
);

/** The descriptor for `slug`, or `null` when nothing is registered under it. */
export function toolServiceDescriptor(slug: string): ToolServiceDescriptor | null {
  return BY_SLUG.get(slug) ?? null;
}

/** A descriptor's fields split into the two connect-payload groups. */
export function toolServiceFieldsByGroup(descriptor: ToolServiceDescriptor): {
  readonly config: readonly ToolServiceField[];
  readonly secret: readonly ToolServiceField[];
} {
  return {
    config: descriptor.fields.filter((f) => !f.secret),
    secret: descriptor.fields.filter((f) => f.secret),
  };
}

/**
 * Render a connect payload into the BOX ENV a runner needs — the ONE place a
 * descriptor's `env` map (box var → field name) is resolved to values, and the exact
 * payload the vault seals (`ToolServiceCredentialSchema`).
 *
 * It runs at CONNECT time, in `apps/api`, because that is the only moment both
 * halves are plaintext in one process: the secret fields arrive on the request and
 * the non-secret ones with them. The assigner that later ships the blob never
 * decrypts, and the box that unwraps it gets a finished `{ VAR: value }` map — so
 * neither has to know a service's field names, and the ciphertext AEAD-binds the
 * VAR to its value.
 *
 * A field with no value (an optional non-secret the human left blank AND the
 * descriptor gives no default) contributes NO var — an empty string would look to
 * the tool like a configured-but-broken endpoint rather than an unset one. A
 * non-string value is skipped for the same reason: the env is strings, and coercing
 * an object into one produces `[object Object]`, not a diagnosis.
 */
export function toolServiceBoxEnv(
  descriptor: ToolServiceDescriptor,
  values: {
    config?: Readonly<Record<string, unknown>>;
    secret?: Readonly<Record<string, unknown>>;
  },
): Record<string, string> {
  const env: Record<string, string> = {};
  for (const [boxVar, fieldName] of Object.entries(descriptor.env)) {
    const value = values.secret?.[fieldName] ?? values.config?.[fieldName];
    if (typeof value !== 'string' || value.length === 0) continue;
    env[boxVar] = value;
  }
  return env;
}
